// Admin Chat Logs viewer — reads /api/admin/assistant/* endpoints.
// View-only: search, paginate, browse transcript. No delete / export.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageCircle, Loader2, Search, ChevronLeft, ChevronRight, Users, Globe, User, Bot, X } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

const fmt = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const escapeHtml = (s = '') => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
const renderMd = (text = '') =>
  escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-emerald-900/40 text-emerald-300 text-[12px]">$1</code>')
    .replace(/\n/g, '<br/>');

export default function ChatLogsTab() {
  const [kind, setKind] = useState('client');           // 'client' | 'public'
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);      // {session_id, kind, ...}
  const [transcript, setTranscript] = useState(null);
  const [txLoading, setTxLoading] = useState(false);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/assistant/sessions', {
        params: { kind, search: debounced || undefined, page, page_size: PAGE_SIZE },
      });
      setSessions(res.data?.sessions || []);
      setTotal(res.data?.total || 0);
    } catch {
      toast.error('Failed to load chat sessions');
    } finally { setLoading(false); }
  }, [kind, debounced, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected(null); setTranscript(null); }, [kind]);

  const openTranscript = async (s) => {
    setSelected(s);
    setTranscript(null);
    setTxLoading(true);
    try {
      const res = await api.get(`/admin/assistant/sessions/${s.session_id}/messages`, { params: { kind: s.kind } });
      setTranscript(res.data);
    } catch { toast.error('Failed to load transcript'); } finally { setTxLoading(false); }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const stats = useMemo(() => ({
    total,
    shown: sessions.length,
  }), [total, sessions.length]);

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-chatlogs-tab">
      {/* Header */}
      <div className="mb-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Chat Logs</h2>
            <p className="text-xs text-slate-400 -mt-0.5">Compliance Assistant transcripts — read only</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-[#0f1611] border border-emerald-900/40 rounded-lg p-0.5" role="tablist">
            <button
              onClick={() => setKind('client')}
              data-testid="chatlogs-kind-client"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition ${kind === 'client' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <Users className="w-3.5 h-3.5" /> Signed-in
            </button>
            <button
              onClick={() => setKind('public')}
              data-testid="chatlogs-kind-public"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition ${kind === 'public' ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              <Globe className="w-3.5 h-3.5" /> Public
            </button>
          </div>
        </div>
      </div>

      {/* Search + summary */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={kind === 'client' ? 'Search by client email / name / title…' : 'Search by visitor_id or message content…'}
            className="w-full bg-[#080b09] border border-emerald-900/40 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
            data-testid="chatlogs-search"
          />
        </div>
        <div className="text-xs text-slate-400" data-testid="chatlogs-total">
          Showing <span className="text-white font-medium">{stats.shown}</span> of <span className="text-white font-medium">{stats.total}</span>
        </div>
      </div>

      {/* Sessions table */}
      <div className="rounded-xl border border-emerald-900/40 bg-[#0f1611] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-14 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-14 text-slate-500 text-sm">
            <MessageCircle className="w-8 h-8 text-slate-700 mx-auto mb-2" />
            No chat sessions match the current filters.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#152219] text-[11px] uppercase tracking-wider text-emerald-400/80 text-left">
                <th className="px-4 py-2.5 font-medium">{kind === 'client' ? 'Client' : 'Visitor'}</th>
                <th className="px-4 py-2.5 font-medium">{kind === 'client' ? 'Title' : 'Preview'}</th>
                <th className="px-4 py-2.5 font-medium text-center">Msgs</th>
                <th className="px-4 py-2.5 font-medium">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={s.session_id}
                  onClick={() => openTranscript(s)}
                  className="border-t border-emerald-900/30 hover:bg-[#152219]/60 cursor-pointer transition"
                  data-testid={`chatlogs-row-${s.session_id}`}
                >
                  <td className="px-4 py-2.5">
                    {kind === 'client' ? (
                      <div>
                        <div className="text-white text-sm truncate max-w-[240px]">{s.user_name || '—'}</div>
                        <div className="text-slate-500 text-[11px] truncate max-w-[240px]">{s.user_email || '—'}</div>
                      </div>
                    ) : (
                      <div className="font-mono text-[11px] text-slate-300 truncate max-w-[220px]">{s.visitor_id}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-300 text-sm truncate max-w-[420px]">
                      {kind === 'client' ? s.title : (s.preview || '(no preview)')}
                    </div>
                    {kind === 'client' && s.preview && (
                      <div className="text-slate-500 text-[11px] truncate max-w-[420px] mt-0.5">{s.preview}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-medium">
                      {s.message_count}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                    {fmt(s.updated_at || s.last_message_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {sessions.length > 0 && totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between px-2">
          <div className="text-xs text-slate-400">Page {page} / {totalPages}</div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded border border-emerald-900/40 text-slate-300 hover:bg-[#152219] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="chatlogs-page-prev"
            ><ChevronLeft className="w-3.5 h-3.5" /> Prev</button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded border border-emerald-900/40 text-slate-300 hover:bg-[#152219] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="chatlogs-page-next"
            >Next <ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}

      {/* Transcript modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => { setSelected(null); setTranscript(null); }}
          data-testid="chatlogs-transcript-modal"
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] bg-[#0f1611] border border-emerald-900/40 rounded-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-emerald-900/40 flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-white truncate">
                  {selected.kind === 'client' ? (selected.user_name || selected.user_email) : `Visitor ${selected.visitor_id?.slice(0, 12)}…`}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selected.kind === 'client' ? selected.title : 'Public chat'} · {selected.message_count} msgs · session <span className="font-mono">{selected.session_id.slice(0, 8)}</span>
                </p>
              </div>
              <button
                onClick={() => { setSelected(null); setTranscript(null); }}
                className="p-1.5 rounded hover:bg-[#152219] text-slate-400 hover:text-white transition"
                data-testid="chatlogs-close-modal"
              ><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {txLoading ? (
                <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading transcript…</div>
              ) : !transcript ? null : (transcript.messages || []).length === 0 ? (
                <div className="text-center text-slate-500 py-8">No messages in this session.</div>
              ) : (
                transcript.messages.map((m, idx) => (
                  <div key={idx} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === 'user' ? 'bg-emerald-500 text-white' : 'bg-[#152219] border border-emerald-900/40 text-emerald-300'}`}>
                      {m.role === 'user' ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                    </div>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${m.role === 'user' ? 'bg-emerald-500 text-white' : 'bg-[#152219] text-slate-100 border border-emerald-900/30'}`}>
                      <div dangerouslySetInnerHTML={{ __html: renderMd(m.content) }} />
                      <div className={`text-[10px] mt-1 ${m.role === 'user' ? 'text-emerald-100/70' : 'text-slate-500'}`}>{fmt(m.created_at)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
